const dateFormat = (date) => {
  return new Date(date).toLocaleString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

// API fields with format `date` represent a calendar day, not an instant.
// Building the Date in local time prevents UTC conversion from moving the day.
const calendarDateFormat = (value, options = {}) => {
  if (!value) return '-'

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return '-'

  const [, year, month, day] = match
  const date = new Date(Number(year), Number(month) - 1, Number(day))

  if (Number.isNaN(date.getTime())) return '-'

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options
  })
}

const timeFormat = (date) => {
  return new Date(date).toLocaleString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const currencyFormat = (value, digits = 2) => {
  if (!value) {
    return
  }

  // without this negative numbers are shown like -R$ 10,00
  if (value < 0)
      return `RS ${parseFloat(value).toLocaleString('pt-BR', {
          maximumFractionDigits: digits,
          minimumFractionDigits: digits}
      )}`

  return parseFloat(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: digits
  })
}

const formatBRL = (value) => {
  if (value === null || value === undefined || value === '') return '–'
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return '–'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numeric)
}

const phoneNumber = (value) => {
  if (!value) return

  return value.replace(/\D/g, '')
    .replace(/^(\d{1})/, '($1')
    .replace(/^(\(\d{2})(\d)/, '$1) $2')
    .replace(/(\d)(\d{4})$/, '$1-$2');
}

const cpfFormat = (value) => {
  if (!value) return

  return value.replace(/\D/g, '')
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3}\.\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3}\.\d{3}\.\d{3})(\d)/, '$1-$2');
}

const cpfCnpjFormat = (value) => {
  if (!value) return

  const digits = String(value).replace(/\D/g, '').slice(0, 14)
  if (!digits.length) return ''

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3}\.\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3}\.\d{3}\.\d{3})(\d)/, '$1-$2')
  }

  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2}\.\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{2}\.\d{3}\.\d{3})(\d)/, '$1/$2')
    .replace(/^(\d{2}\.\d{3}\.\d{3}\/\d{4})(\d)/, '$1-$2')
}

const getNextRenewalDate = (updatedAt) => {
  const date = new Date(updatedAt);
  const nextMonth = new Date(date);

  nextMonth.setMonth(date.getMonth() + 1);

  if (nextMonth.getDate() < date.getDate()) {
    nextMonth.setDate(0);
  } else {
    nextMonth.setDate(date.getDate());
  }

  return nextMonth;
}

// DDDs brasileiros válidos (2 dígitos) — evita assumir +55 em números de outros países
// que também tenham 11 dígitos ou 9 na posição do celular BR.
const BR_DDD_VALID = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19,
  21, 22, 24, 27, 28,
  31, 32, 33, 34, 35, 37, 38, 39,
  41, 42, 43, 44, 45, 46, 47, 48, 49,
  51, 53, 54, 55,
  61, 62, 63, 64, 65, 66, 67, 68, 69,
  71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99
]);

/** Converte o valor armazenado (com ou sem +) para E.164. Usado em Input.PhoneInternational e na formatação de exibição. */
const normalizeWhatsAppToE164 = (phoneNumber) => {
  if (!phoneNumber) return "";
  let cleaned = phoneNumber.replace(/[^\d+]/g, "");
  if (!cleaned) return "";

  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  if (cleaned.startsWith("0")) {
    return "+55" + cleaned.substring(1);
  }

  if (cleaned.startsWith("55") && cleaned.length >= 12) {
    return "+" + cleaned;
  }

  // Celular BR: DDD + 9 + 8 dígitos — só força +55 com DDD válido
  if (cleaned.length === 11 && cleaned[2] === "9") {
    const ddd = parseInt(cleaned.slice(0, 2), 10);
    if (BR_DDD_VALID.has(ddd)) {
      return "+55" + cleaned;
    }
  }

  // NANP: 1 + DDD (3) + 7 dígitos (ex.: 13105551298 → +13105551298)
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return "+" + cleaned;
  }

  // Código de país + número (sem +), ex.: 351912345678, 33612345678
  if (cleaned.length >= 12) {
    return "+" + cleaned;
  }

  // API pode devolver E.164 completo sem "+" (ex.: 35555555555 = Albânia +355 55 555 555).
  // Só depois das regras BR/NANP acima, para não confundir com celular nacional.
  if (/^\d{11,15}$/.test(cleaned)) {
    return "+" + cleaned;
  }

  return "";
};

/** Mensagens para códigos de getValidationError() do intl-tel-input (libphonenumber). */
const getIntlTelValidationMessage = (intlTelInputModule, errorCode) => {
  const ve = intlTelInputModule?.utils?.validationError;
  if (!ve || errorCode == null) {
    return 'Número de telefone inválido para o país selecionado.';
  }
  if (errorCode === ve.TOO_SHORT) return 'Número muito curto para o país selecionado.';
  if (errorCode === ve.TOO_LONG) return 'Número muito longo para o país selecionado.';
  if (errorCode === ve.INVALID_COUNTRY_CODE) return 'Código de país inválido.';
  if (errorCode === ve.INVALID_LENGTH) return 'Quantidade de dígitos inválida para o país selecionado.';
  if (errorCode === ve.IS_POSSIBLE_LOCAL_ONLY) return 'Informe o número completo com DDI.';
  return 'Número de telefone inválido para o país selecionado.';
};

const isBrazilianWhatsAppE164 = (e164) => !!(e164 && e164.startsWith("+55"));

const formatWhatsAppNumber = (phoneNumber) => {
  if (!phoneNumber) return "";

  const cleaned = normalizeWhatsAppToE164(phoneNumber);
  if (!cleaned) return phoneNumber;

  const withoutPlus = cleaned.substring(1);
  
  if (withoutPlus.startsWith("55") && withoutPlus.length >= 12) {
    const countryCode = "55";
    const areaCode = withoutPlus.substring(2, 4);
    const number = withoutPlus.substring(4);
    
    if (number.length === 9) {
      return `+${countryCode} (${areaCode}) ${number.substring(0, 5)}-${number.substring(5)}`;
    } else if (number.length === 8) {
      return `+${countryCode} (${areaCode}) ${number.substring(0, 4)}-${number.substring(4)}`;
    } else {
      return `+${countryCode} (${areaCode}) ${number}`;
    }
  }

  // +1 NANP (EUA/Canadá etc.): evita exibir como "+13 105551298"
  if (withoutPlus.startsWith("1") && withoutPlus.length === 11) {
    const rest = withoutPlus.substring(1);
    return `+1 (${rest.substring(0, 3)}) ${rest.substring(3, 6)}-${rest.substring(6)}`;
  }
  
  if (withoutPlus.length > 4) {
    return `+${withoutPlus.substring(0, 2)} ${withoutPlus.substring(2)}`;
  }
  
  return phoneNumber;
};

const dateTimeFormat = (date) => {
  if (!date) return '-';
  
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return '-';
  }
  
  return dateObj.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export {
  dateFormat,
  calendarDateFormat,
  timeFormat,
  currencyFormat,
  formatBRL,
  phoneNumber,
  cpfFormat,
  cpfCnpjFormat,
  getNextRenewalDate,
  formatWhatsAppNumber,
  normalizeWhatsAppToE164,
  getIntlTelValidationMessage,
  isBrazilianWhatsAppE164,
  dateTimeFormat
}
